import React, { useState, useEffect, useCallback } from 'react';
import { Layout, Table, Button, Input, Form, message, Popconfirm, Space, Card, Tag } from 'antd';
import { SearchOutlined, ReloadOutlined, RollbackOutlined, DeleteOutlined, CheckCircleOutlined } from '@ant-design/icons';
import api from '../utils/api';
import AppHeader from '../components/AppHeader';
import { useTheme } from '../context/ThemeContext';

const { Content } = Layout;

const RecycleBinPage = () => {
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const { isDarkMode, getBackgroundColor, getContentBg, getTextColor, getBorderColor } = useTheme();

  const fetchData = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const queryParams = {
        page: params.page || currentPage,
        pageSize: params.pageSize || pageSize,
        ...params
      };
      const res = await api.get('/recycle-bin', { params: queryParams });
      setData(res.data.data);
      setCurrentPage(res.data.page);
      setPageSize(res.data.pageSize);
      setTotal(res.data.total);
    } catch (err) {
      message.error('获取数据失败');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = (values) => {
    const cleanValues = Object.fromEntries(
      Object.entries(values).filter(([_, v]) => v !== undefined && v !== '' && v !== null)
    );
    setCurrentPage(1);
    fetchData({ ...cleanValues, page: 1 });
  };

  const handleReset = () => {
    form.resetFields();
    setCurrentPage(1);
    fetchData({ page: 1 });
  };

  const handleRestore = async (record) => {
    try {
      await api.put(`/recycle-bin/restore/${record.table_name}/${record.id}`);
      message.success('恢复成功');
      fetchData();
    } catch (err) {
      message.error(err.response?.data?.message || '恢复失败');
    }
  };

  const handlePermanentDelete = async (record) => {
    try {
      await api.delete(`/recycle-bin/permanent/${record.table_name}/${record.id}`);
      message.success('永久删除成功');
      fetchData();
    } catch (err) {
      message.error(err.response?.data?.message || '删除失败');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60, align: 'center' },
    { title: '图纸编号', dataIndex: 'drawing_no', width: 150, align: 'center' },
    { title: '制图人', dataIndex: 'creator', width: 100, align: 'center' },
    { 
      title: '制图日期', 
      dataIndex: 'draw_date', 
      width: 120, 
      align: 'center',
      render: (text) => text ? text.split('T')[0] : '-'
    },
    { title: '机架类型', dataIndex: 'frame_type', width: 100, align: 'center' },
    { title: '探头类型', dataIndex: 'probe_type', width: 100, align: 'center' },
    { title: '探头开口尺寸', dataIndex: 'probe_size', width: 120, align: 'center' },
    { title: '可通过尺寸', dataIndex: 'passable_size', width: 120, align: 'center', ellipsis: true },
    { title: '探头外形尺寸', dataIndex: 'probe_outer_size', width: 130, align: 'center', ellipsis: true },
    { title: '机架长度', dataIndex: 'frame_length', width: 100, align: 'center' },
    { title: '整机外形尺寸', dataIndex: 'machine_outer_size', width: 130, align: 'center', ellipsis: true },
    { title: '皮带输送面离地高度', dataIndex: 'belt_height', width: 150, align: 'center', ellipsis: true },
    { title: '皮带运行方向', dataIndex: 'belt_direction', width: 120, align: 'center' },
    { title: '控制箱与机身设计', dataIndex: 'control_box_design', width: 130, align: 'center' },
    { title: '报警剔除方式', dataIndex: 'alarm_reject_mode', width: 120, align: 'center', ellipsis: true },
    { 
      title: '其他特点', 
      dataIndex: 'other_features', 
      width: 120, 
      align: 'center', 
      ellipsis: true 
    },
    { 
      title: '删除人', 
      dataIndex: 'deleted_by_name', 
      width: 100, 
      align: 'center'
    },
    {
      title: '删除时间',
      dataIndex: 'deleted_at',
      width: 170,
      align: 'center',
      render: (text) => text ? new Date(text).toLocaleString('zh-CN') : '-'
    },
    {
      title: '状态',
      dataIndex: 'restore_status',
      width: 100,
      align: 'center',
      fixed: 'right',
      render: (status) => {
        if (status === 'restored') {
          return <Tag color="green" icon={<CheckCircleOutlined />}>已恢复</Tag>;
        }
        return <Tag color="red">已删除</Tag>;
      }
    },
    {
      title: '操作',
      width: 200,
      fixed: 'right',
      align: 'center',
      render: (_, record) => {
        const isRestored = record.restore_status === 'restored';
        
        return (
          <Space>
            <Button 
              type="primary" 
              size="small" 
              icon={<RollbackOutlined />}
              disabled={isRestored}
              onClick={() => handleRestore(record)}
            >
              {isRestored ? '已恢复' : '撤回删除'}
            </Button>
            <Popconfirm
              title="确定要永久删除吗？"
              description="此操作不可恢复，将同时删除物理文件"
              onConfirm={() => handlePermanentDelete(record)}
              okText="确认"
              cancelText="取消"
            >
              <Button 
                danger 
                size="small" 
                icon={<DeleteOutlined />}
              >
                永久删除
              </Button>
            </Popconfirm>
          </Space>
        );
      }
    }
  ];

  return (
    <Layout style={{ minHeight: '100vh', background: getBackgroundColor() }}>
      <AppHeader />
      <Content style={{ padding: 24 }}>
        <h2 style={{ marginBottom: 16, color: getTextColor() }}>回收站</h2>
        
        <Card style={{ 
          marginBottom: 24,
          background: getContentBg(),
          border: `1px solid ${getBorderColor()}`
        }}>
          <Form form={form} layout="inline" onFinish={handleSearch}>
            <Form.Item name="drawing_no" label={<span style={{ color: getTextColor() }}>图纸编号</span>}>
              <Input placeholder="支持模糊查询" style={{ width: 200 }} allowClear />
            </Form.Item>
            <Form.Item name="creator" label={<span style={{ color: getTextColor() }}>制图人</span>}>
              <Input placeholder="输入制图人" style={{ width: 150 }} allowClear />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>查询</Button>
                <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>

        <Table
          columns={columns}
          dataSource={data}
          rowKey={(record) => `${record.table_name}_${record.id}`}
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条记录`
          }}
          bordered
          scroll={{ x: 'max-content' }}
          size="small"
        />
      </Content>
    </Layout>
  );
};

export default RecycleBinPage;